import type { CSSProperties } from 'react';
import React from 'react';
import { TimelineHeadersConsumer } from './HeadersContext';
import { LEFT_VARIANT, RIGHT_VARIANT } from './constants';

export type RootProps = {
  style?: CSSProperties;
};

export type SidebarChildrenRenderer<Data> = React.ComponentType<{
  getRootProps: (props?: RootProps) => RootProps;
  data: Data | undefined;
}>;

export type SidebarVariant = typeof LEFT_VARIANT | typeof RIGHT_VARIANT;

export type SidebarHeaderProps<Data = object> = {
  children: SidebarChildrenRenderer<Data>;
  rightSidebarWidth?: number;
  leftSidebarWidth: number;
  variant?: SidebarVariant;
  headerData?: Data;
};

class SidebarHeader extends React.PureComponent<SidebarHeaderProps> {
  getRootProps = (props: RootProps = {}): RootProps => {
    const { style } = props;
    const isRight = this.props.variant === RIGHT_VARIANT;
    const width = isRight
      ? this.props.rightSidebarWidth
      : this.props.leftSidebarWidth;
    return {
      style: {
        zIndex: 100,
        position: 'sticky',
        [isRight ? 'right' : 'left']: 0,
        minWidth: width,
        ...style,
        width,
      },
    };
  };

  getStateAndHelpers = () => {
    return {
      getRootProps: this.getRootProps,
      data: this.props.headerData,
    };
  };

  render() {
    const props = this.getStateAndHelpers();
    const Renderer = this.props.children;
    return <Renderer {...props} />;
  }
}

export type SidebarWrapperProps<Data = object> = {
  children?: SidebarChildrenRenderer<Data>;
  variant?: SidebarVariant;
  headerData?: Data;
};

const SidebarWrapper = ({
  children,
  variant,
  headerData,
}: SidebarWrapperProps) => (
  <TimelineHeadersConsumer>
    {({ leftSidebarWidth, rightSidebarWidth }) => {
      return children ? (
        <SidebarHeader
          leftSidebarWidth={leftSidebarWidth}
          rightSidebarWidth={rightSidebarWidth}
          variant={variant}
          headerData={headerData}
        >
          {children}
        </SidebarHeader>
      ) : null;
    }}
  </TimelineHeadersConsumer>
);

const defaultRenderer: SidebarChildrenRenderer<undefined> = ({
  getRootProps,
}) => <div data-testid="sidebarHeader" {...getRootProps()} />;

SidebarWrapper.defaultProps = {
  variant: LEFT_VARIANT,
  children: defaultRenderer,
};

SidebarWrapper.secretKey = 'SidebarHeader';

export default SidebarWrapper;
